import React, {
  useEffect, useImperativeHandle, forwardRef, useState
} from 'react';
import { PropTypes } from 'prop-types';
import { Container, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormTextField from '@seccon/core/components/FormTextField';
import FormAutocomplete from '@seccon/core/components/FormAutocomplete';

import useEmployeeInfo from '../hooks/useEmployeeInfo';
import formatPhoneField from '../formatPhoneField';
import { ACTION_CREATE, ACTION_UPDATE } from '../constants';

const EmployeeForm = forwardRef(({ initialData, action }, ref) => {
  const {
    control, trigger, formState: { errors }, setValue, getValues
  } = useFormContext({});

  const [personnelOptions, setPersonnelOptions] = useState([]);

  const fetchEmployeeInfo = useEmployeeInfo(action);

  const onValidate = async () => {
    const isValid = await trigger();
    if (isValid) {
      console.log('Validation successful');
    } else {
      console.log('Validation failed');
    }
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isValid = await onValidate();
      return isValid;
    }
  }));

  useEffect(() => {
    if (initialData?.data && action === ACTION_CREATE) {
      const selection = {
        id: initialData.data.userPersonnel?.id,
        fullName: initialData.data.userPersonnel?.fullName || ''
      };

      const employeeName = getValues('foreignTravel.travelingPersonnel.id');

      if (!employeeName) {
        setValue('foreignTravel.travelingPersonnel.id', selection);
        setPersonnelOptions([selection])
        fetchEmployeeInfo(selection);
      }
    }
  }, [initialData.data]);

  const fetchPersonnelOptions = async (searchKeywordRaw) => {
    // Change the format from "LastName, FirstName" to "FirstName LastName"
    const searchKeyword = searchKeywordRaw.trim();
    const searchTerm = searchKeyword?.split(',')?.map((i) => i.trim())?.reverse()?.join(' ');
    const currentUserPersonnel = initialData?.data?.userPersonnel;
    const currentUserFullName = currentUserPersonnel?.fullName ?? '';
    const currentUserId = currentUserPersonnel?.id ?? null;
    try {
      const response = await fetch(`/referenceData/findPersonnelInVisibleFacilityIds?name=${ searchTerm }`);
      const data = await response.json();
      const flattenedData = data[0].map((item) => ({
        id: item.id,
        fullName: item.name
      }));

      // Handle circumstances where the query for personnel might not contain the current user, because they belong to a different facility
      // If the searchKeyword is empty or if it contains part of the current user's full name
      if (searchKeyword === '' || currentUserFullName.includes(searchKeyword)) {
        // If the current user's personnel was retrieved earlier and the current user is not in the list of personnel, add them
        if (currentUserPersonnel && !data[0].some((item) => item.id === currentUserId)) {
          flattenedData.push({
            id: currentUserId,
            fullName: currentUserFullName
          });
        }
      }

      setPersonnelOptions(flattenedData);
    } catch (error) {
      console.error('Failed to fetch personnel options', error);
    }
  };

  return (
    <Container>
      <form>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormAutocomplete
              control={control}
              formErrors={errors}
              label='Employee Name'
              formFieldId='foreignTravel.travelingPersonnel.id'
              required
              getOptionLabel={(option) => option.fullName ?? ''}
              freeSolo={false}
              options={personnelOptions}
              fetchedOptions={fetchPersonnelOptions}
              readonly={!initialData?.data?.canReportOnBehalfOfOtherPersonnel || action === ACTION_UPDATE}
              noOptionsText='Type to search for personnel'
              onAdditionalMethod={(newValue) => fetchEmployeeInfo(newValue)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Company Name / Facility Name '
              formFieldId='employee.companyName'
              readonly
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Job Title '
              formFieldId='employee.jobTitle'
              readonly
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Company Phone Number '
              formFieldId='employee.companyPhoneNumber'
              readonly
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Company Email '
              formFieldId='employee.companyEmail'
              readonly
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Cell Number '
              formFieldId='foreignTravel.travelingPersonnel.phoneCell'
              required
              onBlur={(e) => formatPhoneField('foreignTravel.travelingPersonnel.phoneCell', setValue, e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              formErrors={errors}
              label='Personal Email '
              formFieldId='foreignTravel.travelingPersonnel.emailPersonal'
              required
              validationOptions={{
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format'
                }
              }}
            />
          </Grid>
        </Grid>
      </form>
    </Container>
  );
});

EmployeeForm.propTypes = {
  action: PropTypes.string,
  initialData: PropTypes.object.isRequired
};

EmployeeForm.defaultProps = {
  action: ACTION_CREATE
}

export default EmployeeForm;
